import { ReactElement, useMemo } from "react";
import { EventFormModel } from "./EditEventDialog";
import { EventContent } from "client/Features/Hub/Events/Details/EventContent";
import { localize } from "client_legacy/ndaw/culture";
import { HubItemCategoryModel } from "client/Api/Hub/hub";
import { HubItemNameOnlyCategory } from "client/Features/Hub/Content/common";
import {
	EMPTY_ARRAY,
	ensureNotNil,
	isNil,
	isNotNil
} from "client/Misc/utils";
import { MediaContentModel } from "client/Features/Hub/HubDetailsLayout";
import {
	convertLayoutForDetails,
	convertMediaForDetails,
	convertTagForDetails
} from "client/Features/Hub/converters";
import { HubItemKind } from "client/Api/Hub/hubItemCategories";
import { ContentViewerEmptyBlockSettings, HubItemLayoutContentViewerModel } from "client/Features/Hub/Builder/Viewer/ContentViewer";
import { TagModel } from "client/Components";
import { TimeZone } from "client/DateAndTime/timezones";
import {
	ExistingHubItemImages,
	HubHierarchyListCategoryNodeModel,
	HubHierarchyListNodeModel
} from "client/Api/Hub/common.composer";
import { LocationType } from "client/Api/EventsManagement/eventsManagement";
import { LinkedDocumentPreviewModel } from "client/Features/Hub/LinkedDocumentsPreview";
import { getLinkedDocumentId } from "client/Features/Hub/useAddDocuments";
import { getDocumentKind } from "client_legacy/Documents/fileExtensions";
import { DocumentItemType } from "client/Api/Documents/SharePoint/common";
import { UserIdentityModel } from "client/Api/UserProfile/service";
import { useUserProfileStore } from "client/Features/Settings/UserProfile/userProfileStore";
import { ContentWithRelevant } from "client/Features/Hub/ContentWithRelevant";
import { IValueValidation } from "client/Validation/input";
import { eventDetailsInvalidInput } from "client/Features/Hub/Events/Details/EventDetails";
import { isValidDate } from "client/DateAndTime/dateTimeUtils";
import { tryFindHubCategoryItemNode } from "../../common";

export type PreviewProps = {
	model: EventFormModel;
	createdBy: UserIdentityModel | null;
	validation: IValueValidation<EventFormModel>;
	hasPublicationDate: boolean;
	timezone: TimeZone;
	categoryHierarchy: readonly HubHierarchyListNodeModel[];
	originalImages: ExistingHubItemImages;
};

export const Preview = (props: PreviewProps): ReactElement => {
	const {
		model,
		createdBy,
		validation,
		hasPublicationDate,
		categoryHierarchy,
		originalImages,
		timezone
	} = props;

	const now = new Date();

	const publicationDate = useMemo(
		(): Date => {
			if (!hasPublicationDate || !model.publicationDate) {
				return new Date();
			}

			return model.publicationDate;
		},
		[hasPublicationDate, model.publicationDate]
	);

	const category = useMemo(
		(): HubItemCategoryModel | HubItemNameOnlyCategory => {
			if (isNil(model.categoryId)) {
				return {
					name: localize("_CategoryName")
				};
			}

			const tryFindCategoryResult: TryResult<HubHierarchyListCategoryNodeModel> = tryFindHubCategoryItemNode(categoryHierarchy, model.categoryId);

			if (!tryFindCategoryResult.wasSuccessful) {
				throw new Error(`Category data was not found category id: ${model.categoryId}`);
			}

			return tryFindCategoryResult
				.result
				.category;
		},
		[categoryHierarchy, model.categoryId]
	);

	const media = useMemo(
		(): MediaContentModel => {
			return convertMediaForDetails(
				model.media,
				HubItemKind.Event,
				originalImages.coverImage
			);
		},
		[model.media, originalImages.coverImage]
	);

	const layout = useMemo(
		(): HubItemLayoutContentViewerModel => {
			return convertLayoutForDetails(model.layout, originalImages.layoutImages);
		},
		[model.layout, originalImages.layoutImages]
	);

	const tags = useMemo(
		(): TagModel<number>[] => {
			return model.tags
				.map(convertTagForDetails);
		},
		[model.tags]
	);

	const linkedDocuments = useMemo(
		(): readonly LinkedDocumentPreviewModel[] => {
			return model.linkedDocuments
				.map(x => ({
					id: getLinkedDocumentId(x),
					name: x.name,
					thumbnailKind: getDocumentKind(
						DocumentItemType.File,
						x.extension
					)
				}));
		},
		[model.linkedDocuments]
	);

	const currentUserInfo: UserIdentityModel = ensureNotNil(
		useUserProfileStore(x => x.currentUserIdentity)
	);

	const isBeforeEventStartDate: boolean = isNotNil(model.startTime)
		&& isValidDate(model.startTime)
		&& now < model.startTime;

	const isBeforeSignUpDate = isNotNil(model.signUpDeadline)
		? isValidDate(model.signUpDeadline)
			&& now < model.signUpDeadline
		: isNotNil(model.endTime)
			&& isValidDate(model.endTime)
			&& now < model.endTime;

	return (
		<ContentWithRelevant>
			<ContentWithRelevant.Content>
				<EventContent
					hasData
					title={model.title
						|| {
							kind: "placeholder",
							value: localize("_EnterTitle")
						}
					}
					category={category}
					media={media}
					layout={layout}
					emptyBlockSettings={emptyBlockSettings}
					tags={tags}
					createdBy={model.shouldShowAuthor ? createdBy : null}
					isEditable={false}
					publicationDate={isNil(publicationDate) || validation.for("publicationDate").isValid()
						? publicationDate
						: INVALID_DATE
					}
					startDate={isNil(model.startTime) || validation.for("startTime").isValid()
						? model.startTime
						: INVALID_DATE
					}
					endDate={isNil(model.endTime) || validation.for("endTime").isValid()
						? model.endTime
						: INVALID_DATE
					}
					attendeeCounts={EMPTY_ARRAY}
					availableSpots={validation.for("availableSpots").isValid()
						? model.availableSpots
						: eventDetailsInvalidInput
					}
					externalUrl={validation.for("externalUrl").isValid()
						? model.externalUrl
						: eventDetailsInvalidInput
					}
					isAttendanceEnabled={model.isAttendanceEnabled}
					locationType={model.locationType ?? LocationType.Hybrid}
					location={model.location}
					onlineEventUrl={validation.for("onlineEventUrl").isValid()
						? model.onlineEventUrl
						: eventDetailsInvalidInput
					}
					previewAttendees={EMPTY_ARRAY}
					ticketType={model.ticketType}
					signUpDeadline={isNil(model.signUpDeadline) || validation.for("signUpDeadline").isValid()
						? model.signUpDeadline
						: INVALID_DATE
					}
					lastModifiedDate={now}
					isBeforeEventStartDate={isBeforeEventStartDate}
					isBeforeSignUpDate={isBeforeSignUpDate}
					lastModifiedUser={model.shouldShowAuthor ? currentUserInfo : null}
					timezone={timezone}
					linkedDocumentsMode="preview"
					linkedDocuments={linkedDocuments}
					isInteractive={false}
					hasStatistics={false}
					mode="details-only"
					areCommentsEnabled={false}
					shouldNotRenderEmptyFooter
				/>
			</ContentWithRelevant.Content>
		</ContentWithRelevant>
	);
};
Preview.displayName = "Preview";

const emptyBlockSettings: ContentViewerEmptyBlockSettings = {
	faqElement: {
		question: {
			kind: "placeholder",
			placeholder: localize("_EnterQuestion")
		},
		answer: {
			kind: "placeholder",
			placeholder: localize("_EnterAnswer")
		}
	}
};
const INVALID_DATE: Date = new Date("");